import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, BellOff, Loader2, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { usePushNotifications } from '../hooks/usePushNotifications';
import clsx from 'clsx';

interface Props {
  className?: string;
}

export default function PushNotificationToggle({ className }: Props) {
  const { t } = useTranslation();
  const {
    isSupported,
    isEnabled,
    isLoading,
    permission,
    error,
    enablePushNotifications,
    disablePushNotifications,
  } = usePushNotifications();

  const [showDetails, setShowDetails] = useState(false);

  if (!isSupported) {
    return (
      <div className={clsx('bg-yellow-50 border border-yellow-200 rounded-xl p-4', className)}>
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0" />
          <div>
            <p className="text-yellow-800 font-medium">{t('push_toggle.unsupported_title')}</p>
            <p className="text-yellow-600 text-sm">
              {t('push_toggle.unsupported_body')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handleToggle = async () => {
    if (isEnabled) {
      await disablePushNotifications();
    } else {
      await enablePushNotifications();
    }
  };

  return (
    <div className={clsx('bg-white border border-gray-200 rounded-xl p-4', className)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {isEnabled ? (
            <div className="p-2 bg-green-100 rounded-lg">
              <Bell className="h-5 w-5 text-green-600" />
            </div>
          ) : (
            <div className="p-2 bg-gray-100 rounded-lg">
              <BellOff className="h-5 w-5 text-gray-500" />
            </div>
          )}
          <div>
            <p className="font-medium text-gray-900">{t('push_toggle.title')}</p>
            <p className="text-sm text-gray-500">
              {isEnabled
                ? t('push_toggle.subtitle_enabled')
                : t('push_toggle.subtitle_disabled')}
            </p>
          </div>
        </div>

        <button
          onClick={handleToggle}
          disabled={isLoading}
          className={clsx(
            'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed',
            isEnabled ? 'bg-cyan-500' : 'bg-gray-200'
          )}
        >
          <span
            className={clsx(
              'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
              isEnabled ? 'translate-x-5' : 'translate-x-0'
            )}
          >
            {isLoading && (
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            )}
          </span>
        </button>
      </div>

      {/* Status/Error messages */}
      {error && (
        <div className="mt-3 flex items-center gap-2 text-red-600 text-sm">
          <XCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {isEnabled && !error && (
        <div className="mt-3 flex items-center gap-2 text-green-600 text-sm">
          <CheckCircle className="h-4 w-4 flex-shrink-0" />
          {t('push_toggle.active')}
        </div>
      )}

      {permission === 'denied' && (
        <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-red-800 text-sm font-medium">{t('push_toggle.denied_title')}</p>
          <p className="text-red-600 text-sm mt-1">
            {t('push_toggle.denied_body')}
          </p>
          <ol className="text-red-600 text-sm mt-2 list-decimal list-inside">
            <li>{t('push_toggle.denied_step_1')}</li>
            <li>{t('push_toggle.denied_step_2')}</li>
            <li>{t('push_toggle.denied_step_3')}</li>
            <li>{t('push_toggle.denied_step_4')}</li>
          </ol>
        </div>
      )}

      {/* Toggle details */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        className="mt-3 text-sm text-gray-500 hover:text-gray-700 underline"
      >
        {showDetails ? t('push_toggle.hide_details') : t('push_toggle.show_details')}
      </button>

      {showDetails && (
        <div className="mt-3 bg-gray-50 rounded-lg p-3 text-sm">
          <p className="text-gray-700">
            <strong>{t('push_toggle.how_it_works')}</strong>
          </p>
          <ul className="mt-2 text-gray-600 list-disc list-inside space-y-1">
            <li>{t('push_toggle.details_item_1')}</li>
            <li>{t('push_toggle.details_item_2')}</li>
            <li>{t('push_toggle.details_item_3')}</li>
            <li>{t('push_toggle.details_item_4')}</li>
          </ul>
          <p className="mt-3 text-gray-500">
            {t('push_toggle.status_label')}: {isEnabled ? t('push_toggle.status_enabled') : t('push_toggle.status_disabled')} |
            {' '}{t('push_toggle.permission_label')}: {permission || t('push_toggle.permission_unrequested')}
          </p>
        </div>
      )}
    </div>
  );
}
